import { Camera } from "./camera";
import type { FxEvent } from "../sim/sim";
import { settings } from "../ui/settings";

// Client-side particle FX. Purely cosmetic, driven by real (render) time, so it
// never touches the deterministic sim. Explosions and tracers get the lion's
// share of the effort (GDD §10). Everything is pooled with hard caps so a
// 100-unit brawl can never blow the 60fps particle budget.

const FAC_SPARK = ["#8be9ff", "#ffb066"];

// ---- Pool sizes (hard caps) ----
const MAX_PARTICLES = 1400;
const MAX_TRACERS = 400;
const MAX_EXPLOSIONS = 90;
const MAX_DECALS = 120;
const MAX_MARKERS = 40;

// Particle kinds.
const P_DEBRIS = 0;
const P_SMOKE = 1;
const P_SPARK = 2;
const P_EMBER = 3;

interface Explosion {
  x: number; y: number; r: number; age: number; life: number; big: boolean; fac: number; seed: number;
}
interface Decal {
  x: number; y: number; r: number; age: number; life: number; smoke: number;
}
interface Marker {
  x: number; y: number; age: number; life: number; kind: number; color: string;
}

export class FxSystem {
  // Struct-of-arrays particle pool (ring-allocated).
  private px = new Float32Array(MAX_PARTICLES);
  private py = new Float32Array(MAX_PARTICLES);
  private pvx = new Float32Array(MAX_PARTICLES);
  private pvy = new Float32Array(MAX_PARTICLES);
  private page = new Float32Array(MAX_PARTICLES);
  private plife = new Float32Array(MAX_PARTICLES);
  private psize = new Float32Array(MAX_PARTICLES);
  private pkind = new Uint8Array(MAX_PARTICLES);
  private pfac = new Uint8Array(MAX_PARTICLES);
  private pcursor = 0;

  private tracers: Float32Array[] = []; // [x0,y0,x1,y1,age,life,fac,w]
  private tcursor = 0;

  private explosions: Explosion[] = [];
  private decals: Decal[] = [];
  private markers: Marker[] = [];

  private trauma = 0;
  shakeX = 0;
  shakeY = 0;

  private rng = 0x2f6e2b1;

  constructor() {
    for (let i = 0; i < MAX_TRACERS; i++) this.tracers.push(new Float32Array(8));
  }

  private rand(): number {
    this.rng = (this.rng * 1103515245 + 12345) & 0x7fffffff;
    return this.rng / 0x7fffffff;
  }

  private emit(x: number, y: number, vx: number, vy: number, life: number, size: number, kind: number, fac: number): void {
    const i = this.pcursor;
    this.pcursor = (this.pcursor + 1) % MAX_PARTICLES;
    this.px[i] = x; this.py[i] = y; this.pvx[i] = vx; this.pvy[i] = vy;
    this.page[i] = 0; this.plife[i] = life; this.psize[i] = size;
    this.pkind[i] = kind; this.pfac[i] = fac;
  }

  consume(events: FxEvent[]): void {
    for (const e of events) {
      if (e.type === "explosion") this.explosion(e.x, e.y, e.radius, e.faction, !!e.big);
      else if (e.type === "spark") this.spark(e.x, e.y, e.faction);
    }
  }

  tracer(x0: number, y0: number, x1: number, y1: number, fac: number, w = 1): void {
    const t = this.tracers[this.tcursor];
    this.tcursor = (this.tcursor + 1) % MAX_TRACERS;
    t[0] = x0; t[1] = y0; t[2] = x1; t[3] = y1; t[4] = 0; t[5] = 0.09; t[6] = fac; t[7] = w;
  }

  /** A stamped order marker (move / attack / reclaim / rally / repair) at a point. */
  addMarker(x: number, y: number, kind: "move" | "attack" | "reclaim" | "rally" | "repair", color: string): void {
    const k = kind === "move" ? 0 : kind === "attack" ? 1 : kind === "reclaim" ? 2 : kind === "rally" ? 3 : 4;
    this.markers.push({ x, y, age: 0, life: 0.7, kind: k, color });
    if (this.markers.length > MAX_MARKERS) this.markers.shift();
  }

  explosion(x: number, y: number, radius: number, fac: number, big: boolean): void {
    if (this.explosions.length < MAX_EXPLOSIONS) {
      this.explosions.push({ x, y, r: radius, age: 0, life: big ? 1.15 : 0.5, big, fac, seed: this.rand() });
    }
    // Debris + fire.
    const n = Math.min(big ? 46 : 16, 6 + (radius | 0));
    for (let i = 0; i < n; i++) {
      const a = this.rand() * Math.PI * 2;
      const sp = (big ? 190 : 95) * (0.3 + this.rand());
      this.emit(x, y, Math.cos(a) * sp, Math.sin(a) * sp,
        0.4 + this.rand() * (big ? 0.9 : 0.4), 1.5 + this.rand() * (big ? 5 : 2.5), P_DEBRIS, fac);
    }
    // Smoke wisps.
    for (let i = 0; i < (big ? 12 : 4); i++) {
      const a = this.rand() * Math.PI * 2;
      const sp = 20 * this.rand();
      this.emit(x, y, Math.cos(a) * sp, Math.sin(a) * sp - 8,
        0.8 + this.rand() * 1.2, radius * (0.25 + this.rand() * 0.3), P_SMOKE, fac);
    }
    // Lingering scorch decal + smoulder (bigger blasts scar the ground longer).
    if (this.decals.length < MAX_DECALS && radius >= 10) {
      this.decals.push({ x, y, r: radius * (big ? 1.1 : 0.7), age: 0, life: big ? 14 : 7, smoke: big ? 1 : 0.5 });
    }
    // Screen shake — capped, and only the big stuff really kicks.
    if (settings.shake) this.trauma = Math.min(1, this.trauma + (big ? 0.7 : Math.min(0.18, radius / 260)));
  }

  spark(x: number, y: number, fac: number): void {
    // Impact sparks vs armour — bright, fast, short.
    for (let i = 0; i < 6; i++) {
      const a = this.rand() * Math.PI * 2;
      const sp = 70 * (0.3 + this.rand());
      this.emit(x, y, Math.cos(a) * sp, Math.sin(a) * sp,
        0.14 + this.rand() * 0.16, 1 + this.rand() * 1.6, P_SPARK, fac);
    }
  }

  update(dt: number): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.page[i] >= this.plife[i]) continue;
      this.page[i] += dt;
      if (this.page[i] >= this.plife[i]) continue;
      this.px[i] += this.pvx[i] * dt;
      this.py[i] += this.pvy[i] * dt;
      const kind = this.pkind[i];
      const drag = kind === P_SMOKE ? 0.4 : kind === P_EMBER ? 1.0 : 3.2;
      this.pvx[i] -= this.pvx[i] * drag * dt;
      this.pvy[i] -= this.pvy[i] * drag * dt;
      if (kind === P_SMOKE) { this.pvy[i] -= 12 * dt; this.psize[i] += 18 * dt; }
      else if (kind === P_EMBER) { this.pvy[i] -= 22 * dt; }
    }
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      const e = this.explosions[i];
      e.age += dt;
      if (e.age >= e.life) this.explosions.splice(i, 1);
    }
    for (let i = this.decals.length - 1; i >= 0; i--) {
      const d = this.decals[i];
      d.age += dt;
      if (d.age >= d.life) { this.decals.splice(i, 1); continue; }
      // Smouldering wreck: occasional ember + smoke wisp for the first while.
      if (d.smoke > 0 && d.age < d.life * 0.6 && this.rand() < dt * 2.2 * d.smoke) {
        this.emit(d.x + (this.rand() - 0.5) * d.r, d.y + (this.rand() - 0.5) * d.r,
          (this.rand() - 0.5) * 6, -10 - this.rand() * 10, 1.0 + this.rand(), d.r * 0.3, P_SMOKE, 0);
        if (this.rand() < 0.4) {
          this.emit(d.x + (this.rand() - 0.5) * d.r * 0.5, d.y + (this.rand() - 0.5) * d.r * 0.5,
            (this.rand() - 0.5) * 12, -20 - this.rand() * 20, 0.5 + this.rand() * 0.4, 1.2, P_EMBER, 1);
        }
      }
    }
    for (let i = this.tracers.length - 1; i >= 0; i--) {
      const t = this.tracers[i];
      if (t[4] >= t[5]) continue;
      t[4] += dt;
    }
    for (let i = this.markers.length - 1; i >= 0; i--) {
      const m = this.markers[i];
      m.age += dt;
      if (m.age >= m.life) this.markers.splice(i, 1);
    }
    // Screen shake trauma → offset (decays fast).
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
    if (this.trauma > 0 && settings.shake) {
      const mag = this.trauma * this.trauma * 16;
      this.shakeX = (this.rand() * 2 - 1) * mag;
      this.shakeY = (this.rand() * 2 - 1) * mag;
    } else {
      this.shakeX = 0; this.shakeY = 0;
    }
  }

  /** Scorch decals — drawn on the ground, under units. */
  drawGround(ctx: CanvasRenderingContext2D, cam: Camera): void {
    for (const d of this.decals) {
      const k = d.age / d.life;
      const sx = cam.worldToScreenX(d.x), sy = cam.worldToScreenY(d.y);
      const r = d.r * cam.zoom;
      const a = (1 - k) * 0.5;
      const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
      grad.addColorStop(0, `rgba(8,6,5,${a})`);
      grad.addColorStop(0.6, `rgba(18,12,8,${a * 0.7})`);
      grad.addColorStop(1, "rgba(20,14,10,0)");
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(sx, sy, r, 0, 7); ctx.fill();
    }
  }

  draw(ctx: CanvasRenderingContext2D, cam: Camera): void {
    // Smoke first (behind).
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.pkind[i] !== P_SMOKE || this.page[i] >= this.plife[i]) continue;
      const k = this.page[i] / this.plife[i];
      const sx = cam.worldToScreenX(this.px[i]), sy = cam.worldToScreenY(this.py[i]);
      ctx.globalAlpha = 0.32 * (1 - k);
      ctx.fillStyle = "#2b2b30";
      ctx.beginPath(); ctx.arc(sx, sy, this.psize[i] * cam.zoom * (0.5 + k), 0, 7); ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Explosions: shockwave ring + layered fireball + white core flash.
    for (const e of this.explosions) {
      const k = e.age / e.life;
      const sx = cam.worldToScreenX(e.x), sy = cam.worldToScreenY(e.y);
      const zr = e.r * cam.zoom;
      // Shockwave ring.
      ctx.globalAlpha = (1 - k) * 0.65;
      ctx.strokeStyle = FAC_SPARK[e.fac] ?? "#fff";
      ctx.lineWidth = Math.max(1, (e.big ? 5 : 2.5) * (1 - k));
      ctx.beginPath(); ctx.arc(sx, sy, zr * (0.4 + k * 1.3), 0, 7); ctx.stroke();
      // Fireball core.
      const fbAlpha = Math.max(0, 1 - k * 1.6);
      if (fbAlpha > 0) {
        const fr = zr * (0.5 + k * 0.55) * (e.big ? 0.95 : 0.72);
        const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, fr);
        grad.addColorStop(0, `rgba(255,255,240,${fbAlpha})`);
        grad.addColorStop(0.35, `rgba(255,200,90,${fbAlpha * 0.95})`);
        grad.addColorStop(0.7, `rgba(255,90,30,${fbAlpha * 0.6})`);
        grad.addColorStop(1, "rgba(120,20,10,0)");
        ctx.globalAlpha = 1;
        ctx.fillStyle = grad;
        ctx.beginPath(); ctx.arc(sx, sy, fr, 0, 7); ctx.fill();
      }
      // Initial white core flash (very brief).
      if (k < 0.16) {
        ctx.globalAlpha = (1 - k / 0.16);
        ctx.fillStyle = "#ffffff";
        ctx.beginPath(); ctx.arc(sx, sy, zr * (e.big ? 0.5 : 0.35), 0, 7); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;

    // Debris, sparks, embers.
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const kind = this.pkind[i];
      if (kind === P_SMOKE || this.page[i] >= this.plife[i]) continue;
      const k = this.page[i] / this.plife[i];
      const sx = cam.worldToScreenX(this.px[i]), sy = cam.worldToScreenY(this.py[i]);
      ctx.globalAlpha = Math.max(0, 1 - k);
      if (kind === P_SPARK) ctx.fillStyle = FAC_SPARK[this.pfac[i]] ?? "#fff";
      else if (kind === P_EMBER) ctx.fillStyle = k < 0.5 ? "#ffd27a" : "#ff7a2a";
      else ctx.fillStyle = k < 0.4 ? "#ffd27a" : "#ff6a2a";
      const s = Math.max(0.8, this.psize[i] * cam.zoom);
      ctx.fillRect(sx - s / 2, sy - s / 2, s, s);
    }
    ctx.globalAlpha = 1;

    // Tracer bolts (bright glow + white core).
    ctx.lineCap = "round";
    for (let i = 0; i < this.tracers.length; i++) {
      const t = this.tracers[i];
      if (t[4] >= t[5]) continue;
      const a = Math.max(0, 1 - t[4] / t[5]);
      const x0 = cam.worldToScreenX(t[0]), y0 = cam.worldToScreenY(t[1]);
      const x1 = cam.worldToScreenX(t[2]), y1 = cam.worldToScreenY(t[3]);
      ctx.globalAlpha = a * 0.5;
      ctx.strokeStyle = FAC_SPARK[t[6]] ?? "#fff";
      ctx.lineWidth = t[7] * 3 * cam.zoom;
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      ctx.globalAlpha = a;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = Math.max(1, t[7] * 1.2 * cam.zoom);
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.lineCap = "butt";

    this.drawMarkers(ctx, cam);
  }

  private drawMarkers(ctx: CanvasRenderingContext2D, cam: Camera): void {
    for (const m of this.markers) {
      const k = m.age / m.life;
      const sx = cam.worldToScreenX(m.x), sy = cam.worldToScreenY(m.y);
      const a = 1 - k;
      ctx.globalAlpha = a;
      ctx.strokeStyle = m.color;
      ctx.lineWidth = 2;
      if (m.kind === 1) {
        // attack: expanding cross-hair ring.
        const r = 6 + k * 12;
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, 7); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(sx - r - 3, sy); ctx.lineTo(sx - r + 3, sy);
        ctx.moveTo(sx + r - 3, sy); ctx.lineTo(sx + r + 3, sy);
        ctx.moveTo(sx, sy - r - 3); ctx.lineTo(sx, sy - r + 3);
        ctx.moveTo(sx, sy + r - 3); ctx.lineTo(sx, sy + r + 3);
        ctx.stroke();
      } else if (m.kind === 2) {
        // reclaim: rotating brackets.
        const r = 8 + k * 6;
        for (let q = 0; q < 4; q++) {
          const ang = q * Math.PI / 2 + k * 2;
          const cx = sx + Math.cos(ang) * r, cy = sy + Math.sin(ang) * r;
          ctx.beginPath(); ctx.arc(cx, cy, 1.6, 0, 7); ctx.stroke();
        }
      } else if (m.kind === 4) {
        // repair / resume: collapsing ring + plus (wrench-plus motif).
        const r = 12 * (1 - k) + 4;
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, 7); ctx.stroke();
        const p = 4;
        ctx.beginPath();
        ctx.moveTo(sx - p, sy); ctx.lineTo(sx + p, sy);
        ctx.moveTo(sx, sy - p); ctx.lineTo(sx, sy + p);
        ctx.stroke();
      } else {
        // move / rally: collapsing chevron ring.
        const r = 14 * (1 - k) + 4;
        ctx.beginPath(); ctx.arc(sx, sy, r, 0, 7); ctx.stroke();
        if (m.kind === 3) {
          ctx.beginPath(); ctx.moveTo(sx, sy - 3); ctx.lineTo(sx, sy + 4); ctx.stroke();
        }
      }
    }
    ctx.globalAlpha = 1;
  }
}
